import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CountryFlag, GlobeInline } from "@/lib/identity";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * <CountryPicker value="NG" onChange={code => …} />
 *
 * Searchable list of all 54 African countries + "Rest of World" bucket.
 * Pulls /api/countries (filtered by active_participation=true) on mount.
 * Grouped by region. Playoff-eligible countries (NG/GH) are pinned to the top.
 */
export default function CountryPicker({ value, onChange, disabled = false, testidPrefix = "country" }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/countries")
      .then(({ data }) => setList(data?.countries || []))
      .catch(() => setList([]));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const items = needle
      ? list.filter((c) =>
          c.country_name.toLowerCase().includes(needle) ||
          c.country_code.toLowerCase().includes(needle))
      : list;
    // Pin playoff-eligible first, then other African by region, then ROW last.
    return [...items].sort((a, b) => {
      if (a.playoff_eligible !== b.playoff_eligible) return a.playoff_eligible ? -1 : 1;
      if (a.is_african !== b.is_african) return a.is_african ? -1 : 1;
      return (a.country_name || "").localeCompare(b.country_name || "");
    });
  }, [list, q]);

  return (
    <div className="border border-nk-line rounded-sm">
      <div className="flex items-center gap-2 border-b border-nk-line/60 px-3 py-2">
        <MagnifyingGlass size={14} className="text-nk-mute" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search countries…"
          data-testid={`${testidPrefix}-search`}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-nk-dim"
        />
      </div>
      <div className="max-h-72 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-nk-dim">No countries match "{q}".</p>
        ) : (
          <ul className="divide-y divide-nk-line/40">
            {filtered.map((c) => {
              const active = value === c.country_code;
              return (
                <li key={c.country_code}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(c.country_code)}
                    data-testid={`${testidPrefix}-option-${c.country_code.toLowerCase()}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors ${
                      active ? "bg-nk-accent/10 text-nk-text" : "text-nk-mute hover:bg-nk-surface hover:text-nk-text"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <span className="shrink-0">
                      {c.country_code === "ROW" ? <GlobeInline size={16} /> : <CountryFlag country={c.country_code} size={18} />}
                    </span>
                    <span className="flex-1 truncate">{c.country_name}</span>
                    {c.playoff_eligible && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded-sm bg-nk-gold/15 text-nk-gold border border-nk-gold/30 shrink-0">
                        Playoff
                      </span>
                    )}
                    {!c.playoff_eligible && c.is_african && (
                      <span className="text-[10px] text-nk-dim shrink-0">{c.region}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="px-3 py-2 border-t border-nk-line/60 text-[10px] text-nk-dim">
        Championship playoffs currently active in Ghana and Nigeria. More countries unlock as the league grows.
      </p>
    </div>
  );
}
